import type { Document, DocumentRevision } from '../model/types';

export type SearchDocumentRevision = Omit<DocumentRevision, 'document'>;

export type SearchDocument = Omit<Document, 'prefix' | 'revisions'> & {
  prefix?: string;
  sequence?: number | null;
  documentIdentity?: {
    code: number;
    prefix?: {
      code: string;
    } | null;
  } | null;
  revisions?: SearchDocumentRevision[];
  /**
   * Revision that stands for the document in the row, when the source provides one. It is the
   * revision whose sort key put the row where it is, so it, and not the highest-numbered one, is
   * the one to render; sorting client-side and taking the first would show one revision in the
   * slot another revision earned.
   */
  representingRevisionId?: number;
};

export interface DocumentSelectionRow {
  documentId: number;
  representingRevision: DocumentRevision;
  olderRevisions: DocumentRevision[];
}

export type DocumentSelectionSearchField = 'name' | 'prefix';

export function buildDocumentPrefix(document: SearchDocument): string {
  if (document.prefix && document.prefix.trim() !== '') {
    return document.prefix;
  }

  const identityCode = document.documentIdentity?.code;
  const prefixCode = document.documentIdentity?.prefix?.code;

  if (identityCode == null || !prefixCode || document.sequence == null) {
    return '';
  }

  return `${prefixCode}${String(identityCode).padStart(2, '0')}.${document.sequence}`;
}

export function getTimestamp(value: string | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function sortRevisions(revisions: SearchDocumentRevision[]): SearchDocumentRevision[] {
  return [...revisions].sort((left, right) => {
    if (left.revision !== right.revision) {
      return right.revision - left.revision;
    }

    return getTimestamp(right.updatedAt) - getTimestamp(left.updatedAt);
  });
}

export function formatRevisionLabel(revision: number | undefined): string {
  if (revision === undefined) {
    return '-';
  }

  return String(revision).padStart(2, '0');
}

export function buildDocumentSelectionRows(documents: SearchDocument[]): DocumentSelectionRow[] {
  return documents
    .map((document) => {
      const normalizedDocument = {
        ...document,
        prefix: buildDocumentPrefix(document),
      } as Document;

      const normalizedRevisions = sortRevisions(document.revisions ?? []).map((revision) => ({
        ...revision,
        document: normalizedDocument,
      })) as DocumentRevision[];

      if (normalizedRevisions.length === 0) {
        return null;
      }

      const representingIndex =
        document.representingRevisionId === undefined
          ? -1
          : normalizedRevisions.findIndex(
              (revision) => revision.id === document.representingRevisionId
            );
      const representingRevision =
        representingIndex === -1 ? normalizedRevisions[0] : normalizedRevisions[representingIndex];

      return {
        documentId: document.id,
        representingRevision,
        olderRevisions: normalizedRevisions.filter(
          (revision) => revision.id !== representingRevision.id
        ),
      };
    })
    .filter((row): row is DocumentSelectionRow => row !== null);
}

export function upsertSelectedRevisionRecord(
  records: DocumentRevision[],
  revision: DocumentRevision
): DocumentRevision[] {
  const index = records.findIndex((item) => item.id === revision.id);

  if (index === -1) {
    return [...records, revision];
  }

  return records.map((item, itemIndex) => (itemIndex === index ? revision : item));
}

export function getDocumentRowRevisions(row: DocumentSelectionRow): DocumentRevision[] {
  return [row.representingRevision, ...row.olderRevisions];
}

export function getAvailableSelectionRevisions(rows: DocumentSelectionRow[]): DocumentRevision[] {
  return rows.flatMap(getDocumentRowRevisions);
}

export interface BuildDocumentSelectionSearchParamsOptions {
  latestRevisionOnly?: boolean;
}

export function buildDocumentSelectionSearchParams(
  search: string,
  searchType: DocumentSelectionSearchField,
  page: number,
  amount: number,
  searchUrl: string = '/documents/search',
  options: BuildDocumentSelectionSearchParamsOptions = {}
) {
  return {
    search,
    searchType,
    sort: {},
    filter: {},
    page,
    amount,
    searchUrl,
    ...(options.latestRevisionOnly ? { latestRevisionOnly: 'true' } : {}),
  };
}

export function isSelectionRowExpanded(
  row: DocumentSelectionRow,
  expandedDocumentIds: number[],
  isOlderRevisionSelected: (revision: DocumentRevision) => boolean
): boolean {
  return (
    expandedDocumentIds.includes(row.documentId) || row.olderRevisions.some(isOlderRevisionSelected)
  );
}

export function toggleExpandedDocumentIds(
  expandedDocumentIds: number[],
  documentId: number
): number[] {
  return expandedDocumentIds.includes(documentId)
    ? expandedDocumentIds.filter((id) => id !== documentId)
    : [...expandedDocumentIds, documentId];
}

export function filterExpandedDocumentIds(
  expandedDocumentIds: number[],
  rows: DocumentSelectionRow[]
): number[] {
  return expandedDocumentIds.filter((documentId) =>
    rows.some((row) => row.documentId === documentId)
  );
}

export function syncSelectedRevisionRecords(
  rows: DocumentSelectionRow[],
  selectedRevisionIds: number[],
  selectedRevisionRecords: DocumentRevision[]
): DocumentRevision[] {
  return getAvailableSelectionRevisions(rows)
    .filter((revision) => selectedRevisionIds.includes(revision.id))
    .reduce(upsertSelectedRevisionRecord, selectedRevisionRecords);
}

export function removeSelectedRevision(
  selectedRevisionIds: number[],
  selectedRevisionRecords: DocumentRevision[],
  revisionId: number
): {
  selectedRevisionIds: number[];
  selectedRevisionRecords: DocumentRevision[];
} {
  return {
    selectedRevisionIds: selectedRevisionIds.filter((id) => id !== revisionId),
    selectedRevisionRecords: selectedRevisionRecords.filter(
      (revision) => revision.id !== revisionId
    ),
  };
}

export function getVisibleSelectionRevisions(
  rows: DocumentSelectionRow[],
  isExpanded: (row: DocumentSelectionRow) => boolean
): DocumentRevision[] {
  return rows.flatMap((row) =>
    isExpanded(row) ? getDocumentRowRevisions(row) : [row.representingRevision]
  );
}
